<template>
  <div class="contract-timeline-component">
    <!-- Pestañas de Vista / Líneas de Tiempo -->
    <v-card flat class="mb-4 bg-transparent">
      <v-tabs
        v-model="activeTab"
        color="primary"
        active-class="font-weight-bold"
        dense
        background-color="grey lighten-4"
        class="rounded-lg"
      >
        <v-tab class="text-none caption px-4">
          <v-icon small left color="primary">mdi-timeline-text-outline</v-icon>
          1. Contratos y Movimientos (Salud, Pensión, Banco, SCTR)
        </v-tab>
        <v-tab class="text-none caption px-4">
          <v-icon small left color="indigo">mdi-account-tie</v-icon>
          2. Historial de Puestos / Cargos
        </v-tab>
      </v-tabs>
    </v-card>

    <!-- Loader de contratos -->
    <div v-if="loading" class="text-center py-8">
      <v-progress-circular indeterminate color="primary" size="40" />
      <div class="caption text--secondary mt-2">
        Cargando contratos y movimientos...
      </div>
    </div>

    <!-- Sin contratos -->
    <div
      v-else-if="!contracts || contracts.length === 0"
      class="text-center py-10 empty-state"
    >
      <v-icon size="56" color="grey lighten-1"
        >mdi-file-document-outline</v-icon
      >
      <div
        class="text-subtitle-1 font-weight-medium grey--text text--darken-1 mt-2"
      >
        Sin contratos registrados
      </div>
      <div class="text-caption grey--text">
        El trabajador aún no cuenta con historial de contratos o movimientos
        laborales.
      </div>
    </div>

    <!-- Vistas por Pestaña -->
    <div v-else>
      <!-- TAB 0: HISTORIAL DE PUESTOS / CARGOS -->
      <v-card flat outlined class="pa-4 rounded-lg mb-4" v-if="activeTab === 1">
        <div class="d-flex align-center justify-space-between mb-4">
          <div>
            <div
              class="text-subtitle-2 font-weight-bold primary--text mb-0 d-flex align-center"
            >
              <v-icon color="primary" small class="mr-2"
                >mdi-briefcase-outline</v-icon
              >
              Trayectoria de Puestos y Cargos Laborales
            </div>
            <div class="caption text--secondary">
              Historial cronológico de cargos ejercidos por el colaborador en la
              empresa
            </div>
          </div>
          <v-btn
            x-small
            text
            color="primary"
            @click="fetchAllMovements(true)"
            :loading="loadingAnyMovement"
          >
            <v-icon x-small left>mdi-refresh</v-icon> Actualizar
          </v-btn>
        </div>

        <div
          v-if="positionsTimeline.length === 0"
          class="text-center py-6 grey--text"
        >
          <v-icon color="grey lighten-1">mdi-briefcase-search-outline</v-icon>
          <div class="caption mt-1">No se registran cambios de puesto.</div>
        </div>

        <v-timeline v-else dense align-top class="positions-timeline pt-2">
          <v-timeline-item
            v-for="(item, pIdx) in positionsTimeline"
            :key="pIdx"
            :color="
              item.isCut
                ? 'amber darken-2'
                : !item.EndDate || item.AgtStatus == 1
                ? 'teal darken-1'
                : 'indigo darken-1'
            "
            :icon="item.isCut ? 'mdi-link-variant-off' : 'mdi-briefcase-account'"
            small
            fill-dot
            class="pb-3"
          >
            <!-- TARJETA DE CORTE / INTERRUPCIÓN LABORAL -->
            <v-card
              v-if="item.isCut"
              flat
              outlined
              class="pa-3 rounded-lg"
              style="background-color: #fffdf5; border-color: #f59e0b !important; border-style: dashed !important; border-width: 1.5px !important;"
            >
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <v-icon small color="amber darken-3" class="mr-2"
                    >mdi-alert-circle-outline</v-icon
                  >
                  <span
                    class="font-weight-bold text-subtitle-2 amber--text text--darken-4"
                  >
                    Interrupción Laboral / Corte de Continuidad
                  </span>
                </div>
                <v-chip
                  x-small
                  color="amber lighten-4"
                  text-color="amber darken-4"
                  class="font-weight-bold"
                >
                  {{ item.gapDays }} {{ item.gapDays === 1 ? "día" : "días" }} sin contrato
                </v-chip>
              </div>
              <div
                class="caption text--secondary mt-1 d-flex align-center flex-wrap"
              >
                <span>
                  <v-icon x-small color="amber darken-3" class="mr-1"
                    >mdi-calendar-remove</v-icon
                  >
                  <strong>Periodo de corte:</strong> {{ formatDate(item.StartDate) }} al
                  {{ formatDate(item.EndDate) }}
                </span>
                <span class="ml-2 grey--text">
                  (Corte entre Contrato #{{ item.prevAgtID }} y Contrato #{{ item.nextAgtID }})
                </span>
              </div>
            </v-card>

            <!-- TARJETA DE PUESTO / CARGO LABORAL -->
            <v-card v-else flat outlined class="pa-3 rounded-lg position-card">
              <div
                class="d-flex align-center justify-space-between flex-wrap mb-1"
              >
                <div class="d-flex align-center flex-wrap">
                  <span
                    class="font-weight-bold text-subtitle-2 text--primary mr-2"
                  >
                    {{ item.PstName }}
                  </span>
                  <v-chip
                    x-small
                    color="indigo lighten-5"
                    text-color="indigo darken-4"
                    class="font-weight-bold mr-2"
                  >
                    {{ item.AreName }}
                  </v-chip>
                  <v-chip
                    v-if="item.isInternalChange"
                    x-small
                    color="amber lighten-5"
                    text-color="amber darken-4"
                    class="font-weight-medium mr-2"
                  >
                    <v-icon left x-small>mdi-swap-horizontal</v-icon>
                    Cambio en contrato vigente
                  </v-chip>
                  <v-chip
                    v-if="item.contracts && item.contracts.length > 1"
                    x-small
                    color="teal lighten-5"
                    text-color="teal darken-4"
                    class="font-weight-medium"
                  >
                    <v-icon left x-small>mdi-link-variant</v-icon>
                    Continuidad en {{ item.contracts.length }} contratos
                  </v-chip>
                </div>
                <v-chip
                  x-small
                  :color="
                    !item.EndDate || item.AgtStatus == 1
                      ? 'green lighten-5'
                      : 'grey lighten-3'
                  "
                  :text-color="
                    !item.EndDate || item.AgtStatus == 1
                      ? 'green darken-3'
                      : 'grey darken-2'
                  "
                  class="font-weight-medium"
                >
                  {{
                    !item.EndDate || item.AgtStatus == 1
                      ? "Activo / Vigente"
                      : "Finalizado"
                  }}
                </v-chip>
              </div>

              <div
                class="caption text--secondary d-flex align-center flex-wrap gap-2 mt-1"
              >
                <span>
                  <v-icon x-small color="indigo" class="mr-1"
                    >mdi-calendar-range</v-icon
                  >
                  <strong>Vigencia:</strong> {{ formatDate(item.StartDate) }} —
                  {{ item.EndDate ? formatDate(item.EndDate) : "Actualidad" }}
                </span>
                <span
                  v-if="formatPeriodDuration(item.StartDate, item.EndDate)"
                  class="font-weight-medium text--primary"
                >
                  ({{ formatPeriodDuration(item.StartDate, item.EndDate) }})
                </span>
                <span v-if="item.CceName && item.CceName !== '—'">
                  · <strong>C. Costo:</strong> {{ item.CceName }}
                </span>
                <span v-if="item.contracts && item.contracts.length > 0">
                  ·
                  <strong>{{
                    item.contracts.length > 1 ? "Contratos:" : "Contrato:"
                  }}</strong>
                  #{{ item.contracts.join(", #") }}
                </span>
              </div>
            </v-card>
          </v-timeline-item>
        </v-timeline>
      </v-card>

      <!-- TAB 1: CONTRATOS Y MOVIMIENTOS COMPLETOS -->
      <div v-if="activeTab === 0">
        <div class="d-flex align-center justify-space-between mb-3 px-1">
          <span class="text-caption text--secondary font-weight-medium">
            Mostrando {{ paginatedContracts.length }} de
            {{ contracts.length }} contratos
          </span>
          <v-btn
            x-small
            text
            color="primary"
            @click="$emit('refresh')"
            :loading="loading"
          >
            <v-icon x-small left>mdi-refresh</v-icon> Refrescar
          </v-btn>
        </div>

        <div
          v-for="contract in paginatedContracts"
          :key="contract.AgtID"
          class="contract-card mb-3"
          :class="{
            'contract-card--active': contract.AgtStatus == 1,
            'contract-card--inactive': contract.AgtStatus != 1,
            'contract-card--selected': selectedAgtID === contract.AgtID,
          }"
        >
          <!-- Header del contrato (Clic para toggle) -->
          <div
            class="contract-header d-flex align-center pa-3"
            @click="toggleContract(contract)"
            style="cursor: pointer"
          >
            <!-- Status Dot -->
            <div
              class="contract-dot mr-3"
              :class="contract.AgtStatus == 1 ? 'dot-active' : 'dot-inactive'"
            />

            <!-- Información principal -->
            <div class="flex-grow-1">
              <div class="d-flex align-center flex-wrap gap-1 mb-1">
                <span class="font-weight-bold text-body-2 mr-2 text--primary">
                  Contrato #{{ contract.AgtID }}
                </span>
                <v-chip
                  x-small
                  :color="getStatusColor(contract.AgtStatus)"
                  :text-color="getStatusTextColor(contract.AgtStatus)"
                  class="font-weight-bold"
                >
                  {{ getStatusLabel(contract.AgtStatus) }}
                </v-chip>

                <v-chip
                  v-if="contract.AgtIsSCTR"
                  x-small
                  color="amber darken-2"
                  text-color="white"
                  class="ml-1 font-weight-medium"
                >
                  <v-icon x-small left>mdi-shield-alert</v-icon>
                  SCTR
                </v-chip>

                <v-chip
                  v-if="contract.AgtRenovation"
                  x-small
                  color="purple lighten-4"
                  text-color="purple darken-3"
                  class="ml-1"
                >
                  Renovación
                </v-chip>
              </div>

              <div class="text-caption text--secondary font-weight-medium">
                {{ contract.TypeContractName || "Contrato de Trabajo" }}
                <template v-if="contract.PstName">
                  ·
                  <span class="text--primary">{{
                    contract.PstName
                  }}</span></template
                >
                <template v-if="contract.AreName">
                  ({{ contract.AreName }})</template
                >
              </div>

              <div
                class="text-caption d-flex align-center mt-1 grey--text text--darken-1"
              >
                <v-icon x-small class="mr-1" color="grey darken-1"
                  >mdi-calendar-range</v-icon
                >
                <span>{{ formatDate(contract.AgtBeginDate) }}</span>
                <span class="mx-1 font-weight-bold">—</span>
                <span>{{
                  contract.AgtEndDate
                    ? formatDate(contract.AgtEndDate)
                    : "Indefinido"
                }}</span>
                <template v-if="contract.AgtTerminationDate">
                  <v-chip
                    x-small
                    color="red lighten-5"
                    text-color="red darken-3"
                    class="ml-2 font-weight-medium"
                  >
                    Cese: {{ formatDate(contract.AgtTerminationDate) }}
                  </v-chip>
                </template>
              </div>
            </div>

            <!-- Acciones y Chevron -->
            <div class="d-flex flex-column align-end ml-2">
              <div class="d-flex align-center">
                <!-- Menú Editar con ámbitos de modificación (bloqueado para contratos de baja) -->
                <v-menu
                  v-if="contract.AgtStatus != 2"
                  offset-y
                  left
                  transition="slide-y-transition"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      x-small
                      text
                      color="primary"
                      class="mr-1"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop
                      title="Editar contrato"
                    >
                      <v-icon x-small left>mdi-pencil</v-icon> Editar
                      <v-icon x-small right>mdi-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense class="py-0 border-radius-4">
                    <v-subheader
                      class="font-weight-bold text-uppercase caption text--secondary py-1 px-3"
                    >
                      ¿Qué desea modificar?
                    </v-subheader>
                    <v-divider />
                    <v-list-item
                      @click.stop="$emit('edit', contract, 'MOD_LABORAL')"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="primary"
                          >mdi-briefcase-edit-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-title class="caption"
                        >Condiciones Laborales (Puesto, Salario,
                        Horario)</v-list-item-title
                      >
                    </v-list-item>

                    <v-list-item
                      @click.stop="$emit('edit', contract, 'MOD_PENSION')"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="indigo"
                          >mdi-shield-account-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-title class="caption"
                        >Régimen Pensionario (AFP / ONP)</v-list-item-title
                      >
                    </v-list-item>

                    <v-list-item
                      @click.stop="$emit('edit', contract, 'MOD_SALUD')"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="teal"
                          >mdi-hospital-building</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-title class="caption"
                        >Régimen de Salud (EsSalud / EPS)</v-list-item-title
                      >
                    </v-list-item>

                    <v-list-item
                      @click.stop="$emit('edit', contract, 'MOD_CUENTA')"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="blue darken-2"
                          >mdi-bank-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-title class="caption"
                        >Cuenta Bancaria de Remuneración</v-list-item-title
                      >
                    </v-list-item>

                    <v-divider />

                    <v-list-item
                      @click.stop="$emit('edit', contract, 'MOD_ALL')"
                    >
                      <v-list-item-icon class="mr-2">
                        <v-icon small color="grey darken-2"
                          >mdi-lock-open-variant-outline</v-icon
                        >
                      </v-list-item-icon>
                      <v-list-item-title class="caption font-weight-medium"
                        >Modificación General (Todos los
                        campos)</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
                <v-btn
                  v-if="
                    contract.AgtStatus == 1 &&
                    !isIndeterminate(contract) &&
                    !contract.AgtRenovation
                  "
                  x-small
                  text
                  color="success"
                  @click.stop="$emit('renovate', contract)"
                  title="Renovar contrato"
                >
                  <v-icon x-small left>mdi-refresh</v-icon> Renovar
                </v-btn>

                <v-btn
                  x-small
                  text
                  color="deep-purple darken-1"
                  class="mr-1 font-weight-medium"
                  @click.stop="openFormatsModal(contract)"
                  title="Ver formatos a firmar (RISST, RIT, Código de conducta, etc.)"
                >
                  <v-icon x-small left>mdi-file-certificate-outline</v-icon>
                  Formatos a Firmar
                </v-btn>
              </div>
              <v-btn icon x-small class="mt-1">
                <v-icon small color="grey darken-1">
                  {{
                    expandedContracts.includes(contract.AgtID)
                      ? "mdi-chevron-up"
                      : "mdi-chevron-down"
                  }}
                </v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Detalle Expandido: Historial de Movimientos -->
          <v-expand-transition>
            <div
              v-if="expandedContracts.includes(contract.AgtID)"
              class="movements-container px-4 pb-3 pt-1"
            >
              <v-divider class="mb-3" />

              <div class="d-flex align-center justify-space-between mb-2">
                <span
                  class="text-caption font-weight-bold text--secondary text-uppercase tracking-wider"
                >
                  <v-icon x-small left color="indigo"
                    >mdi-timeline-text-outline</v-icon
                  >
                  Historial de Movimientos y Modificaciones
                </span>
                <v-btn
                  x-small
                  icon
                  color="primary"
                  @click="fetchMovements(contract.AgtID, true)"
                  :loading="loadingMovements[contract.AgtID]"
                  title="Recargar movimientos"
                >
                  <v-icon x-small>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <!-- Loader de movimientos -->
              <div
                v-if="loadingMovements[contract.AgtID]"
                class="text-center py-4"
              >
                <v-progress-circular
                  indeterminate
                  color="indigo"
                  size="24"
                  width="2"
                />
                <div class="caption text--secondary mt-1">
                  Consultando movimientos...
                </div>
              </div>

              <!-- Sin movimientos -->
              <div
                v-else-if="
                  !movements[contract.AgtID] ||
                  movements[contract.AgtID].length === 0
                "
                class="text-caption grey--text text-center py-4 bg-light rounded"
              >
                <v-icon small color="grey" class="mr-1"
                  >mdi-information-outline</v-icon
                >
                No se registran movimientos para este contrato.
              </div>

              <!-- Timeline de Movimientos -->
              <div v-else class="movements-timeline-wrap">
                <v-timeline dense align-top class="pt-1 movements-timeline">
                  <v-timeline-item
                    v-for="(mov, mIdx) in movements[contract.AgtID]"
                    :key="mov.AmsID || mIdx"
                    :color="resolveMovementColor(mov)"
                    :icon="getMovementIcon(mov.TypeAms)"
                    small
                    fill-dot
                    class="pb-3"
                  >
                    <v-card flat outlined class="movement-detail-card pa-3">
                      <!-- Cabecera del Movimiento -->
                      <div
                        class="d-flex align-center justify-space-between flex-wrap mb-1"
                      >
                        <div class="d-flex align-center flex-wrap">
                          <v-chip
                            small
                            :color="resolveMovementColor(mov)"
                            text-color="white"
                            class="font-weight-bold mr-2"
                            :style="getCustomHexStyle(mov)"
                          >
                            {{ getMovementTypeName(mov) }}
                          </v-chip>
                          <span
                            class="text-caption font-weight-bold text--secondary mr-2"
                          >
                            Movimiento #{{ mov.AmsID }}
                          </span>
                          <v-chip
                            x-small
                            :color="
                              mov.AmsStatus == 1
                                ? 'green lighten-5'
                                : 'grey lighten-3'
                            "
                            :text-color="
                              mov.AmsStatus == 1
                                ? 'green darken-3'
                                : 'grey darken-2'
                            "
                            class="font-weight-medium"
                          >
                            {{
                              mov.AmsStatus == 1
                                ? "Vigente"
                                : "Cerrado/Inactivo"
                            }}
                          </v-chip>
                        </div>

                        <div class="d-flex align-center">
                          <span
                            v-if="mov.SecCreate"
                            class="text-caption grey--text text--darken-1 mr-2"
                            style="font-size: 11px !important"
                          >
                            <v-icon x-small color="grey"
                              >mdi-clock-outline</v-icon
                            >
                            {{ formatDateTime(mov.SecCreate) }}
                          </span>

                          <!-- Botón de anular/eliminar movimiento (bloqueado para Origen) -->
                          <v-tooltip
                            bottom
                            v-if="
                              !isInitialMovement(
                                mov,
                                movements[contract.AgtID],
                                mIdx,
                              ) && contract.AgtStatus != 2
                            "
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                x-small
                                icon
                                color="red lighten-1"
                                v-bind="attrs"
                                v-on="on"
                                @click.stop="
                                  confirmDeleteMovement(mov, contract.AgtID)
                                "
                              >
                                <v-icon x-small>mdi-trash-can-outline</v-icon>
                              </v-btn>
                            </template>
                            <span>Anular movimiento</span>
                          </v-tooltip>
                        </div>
                      </div>

                      <!-- Fechas de Vigencia del Movimiento -->
                      <div
                        class="text-caption grey--text text--darken-2 mb-2 d-flex align-center flex-wrap"
                      >
                        <v-icon x-small class="mr-1" color="indigo"
                          >mdi-calendar-clock</v-icon
                        >
                        <strong>Vigencia Movimiento:</strong>
                        <span class="ml-1 font-weight-medium text--primary">
                          {{
                            formatDate(
                              mov.AmsStartDateMovement || mov.AmsBeginDate,
                            )
                          }}
                        </span>
                        <span class="mx-1 font-weight-bold">—</span>
                        <span
                          class="font-weight-medium"
                          :class="
                            mov.AmsEndDateMovement
                              ? 'text--primary'
                              : 'green--text text--darken-3'
                          "
                        >
                          {{
                            mov.AmsEndDateMovement
                              ? formatDate(mov.AmsEndDateMovement)
                              : mov.AmsEndDate
                              ? formatDate(mov.AmsEndDate)
                              : "Abierto / Vigente"
                          }}
                        </span>
                      </div>

                      <!-- SECCIÓN: CAMBIOS DETECTADOS RESPECTO AL MOVIMIENTO ANTERIOR -->
                      <div
                        v-if="
                          getMovementDiffs(mov, movements[contract.AgtID], mIdx)
                            .length > 0
                        "
                        class="diff-box pa-2 mb-2 rounded"
                      >
                        <div class="diff-header d-flex align-center mb-1">
                          <v-icon x-small color="amber darken-3" class="mr-1"
                            >mdi-compare-horizontal</v-icon
                          >
                          <span
                            class="text-caption font-weight-bold amber--text text--darken-4"
                          >
                            Cambios detectados vs movimiento anterior:
                          </span>
                        </div>
                        <div class="d-flex flex-wrap gap-1">
                          <div
                            v-for="(diff, dIdx) in getMovementDiffs(
                              mov,
                              movements[contract.AgtID],
                              mIdx,
                            )"
                            :key="dIdx"
                            class="diff-badge d-flex align-center px-2 py-1 mr-1 mb-1"
                          >
                            <span class="diff-field-name mr-1"
                              >{{ diff.label }}:</span
                            >
                            <span class="diff-old-val mr-1">{{
                              diff.oldVal || "—"
                            }}</span>
                            <v-icon x-small color="grey darken-1" class="mx-1"
                              >mdi-arrow-right-bold</v-icon
                            >
                            <span class="diff-new-val font-weight-bold">{{
                              diff.newVal || "—"
                            }}</span>
                          </div>
                        </div>
                      </div>

                      <!-- NOTA INICIAL PARA ORIGEN -->
                      <div
                        v-else-if="
                          isInitialMovement(
                            mov,
                            movements[contract.AgtID],
                            mIdx,
                          )
                        "
                        class="initial-box pa-2 mb-2 rounded d-flex align-center"
                      >
                        <v-icon x-small color="indigo" class="mr-1"
                          >mdi-flag-checkered</v-icon
                        >
                        <span
                          class="text-caption font-weight-bold indigo--text text--darken-3"
                        >
                          Condiciones iniciales de contratación (Origen / Alta)
                        </span>
                      </div>

                      <!-- BOTÓN PARA EXPANDIR TODOS LOS CAMPOS -->
                      <div
                        class="d-flex align-center justify-space-between mt-1"
                      >
                        <v-btn
                          x-small
                          text
                          color="indigo darken-2"
                          @click="
                            toggleShowFullFields(
                              mov.AmsID,
                              isInitialMovement(
                                mov,
                                movements[contract.AgtID],
                                mIdx,
                              ),
                            )
                          "
                        >
                          <v-icon x-small left>{{
                            isShowingFullFields(
                              mov.AmsID,
                              isInitialMovement(
                                mov,
                                movements[contract.AgtID],
                                mIdx,
                              ),
                            )
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}</v-icon>
                          {{
                            isShowingFullFields(
                              mov.AmsID,
                              isInitialMovement(
                                mov,
                                movements[contract.AgtID],
                                mIdx,
                              ),
                            )
                              ? "Ocultar resumen completo"
                              : "Ver todos los datos del movimiento"
                          }}
                        </v-btn>
                      </div>

                      <!-- Datos Laborales Completos (Colapsable o para Origen) -->
                      <v-expand-transition>
                        <div
                          v-show="
                            isShowingFullFields(
                              mov.AmsID,
                              isInitialMovement(
                                mov,
                                movements[contract.AgtID],
                                mIdx,
                              ),
                            )
                          "
                          class="mt-2"
                        >
                          <v-row
                            dense
                            class="text-caption grey--text text--darken-3 bg-movement-fields pa-2 rounded"
                          >
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.PstName || mov.PstID"
                            >
                              <strong>Cargo:</strong>
                              {{ mov.PstName || "ID " + mov.PstID }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.AreName || mov.AreID"
                            >
                              <strong>Área:</strong>
                              {{ mov.AreName || "ID " + mov.AreID }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.CceName || mov.CceID"
                            >
                              <strong>Centro de Costo:</strong>
                              {{ mov.CceName || "ID " + mov.CceID }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.TypeContractName || mov.TypeContract"
                            >
                              <strong>Tipo Contrato:</strong>
                              {{ mov.TypeContractName || mov.TypeContract }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.TypeCategoryName || mov.TypeCategory"
                            >
                              <strong>Categoría:</strong>
                              {{ mov.TypeCategoryName || mov.TypeCategory }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.TypeFormatLaborName"
                            >
                              <strong>Mod. Formativa:</strong>
                              {{ mov.TypeFormatLaborName }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.TypeLaborRegimeName"
                            >
                              <strong>Régimen Laboral:</strong>
                              {{ mov.TypeLaborRegimeName }}
                            </v-col>
                            <v-col cols="12" sm="6" v-if="mov.TypeWorkerName">
                              <strong>Tipo Trabajador:</strong>
                              {{ mov.TypeWorkerName }}
                            </v-col>
                            <v-col cols="12" sm="6" v-if="mov.TypeTurnoName">
                              <strong>Turno:</strong> {{ mov.TypeTurnoName }}
                            </v-col>
                            <v-col cols="12" sm="6" v-if="mov.CmpName">
                              <strong>Campaña:</strong> {{ mov.CmpName }}
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="
                                mov.TypePensionRegimenName ||
                                mov.TypePensionRegimen
                              "
                            >
                              <strong>Pensión:</strong>
                              {{
                                mov.TypePensionRegimenName ||
                                "Régimen #" + mov.TypePensionRegimen
                              }}
                              <span v-if="mov.CUSPP">({{ mov.CUSPP }})</span>
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="
                                mov.TypeHealthRegimenName ||
                                mov.TypeHealthRegimen
                              "
                            >
                              <strong>Salud:</strong>
                              {{
                                mov.TypeHealthRegimenName ||
                                "Régimen #" + mov.TypeHealthRegimen
                              }}
                              <span v-if="mov.TypeCompanyName"
                                >({{ mov.TypeCompanyName }})</span
                              >
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="
                                mov.TypeEntityBankName || mov.TypeEntityBank
                              "
                            >
                              <strong>Banco:</strong>
                              {{
                                mov.TypeEntityBankName ||
                                "Banco #" + mov.TypeEntityBank
                              }}
                              <span v-if="mov.BatAccountNumber"
                                >({{ mov.BatAccountNumber }})</span
                              >
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="mov.AgtIsSCTR || mov.AmsScrtBeginDate"
                            >
                              <strong class="amber--text text--darken-3"
                                >SCTR:</strong
                              >
                              <span v-if="mov.AmsScrtBeginDate">{{
                                formatDate(mov.AmsScrtBeginDate)
                              }}</span>
                              <span v-if="mov.AmsScrtEndDate">
                                a {{ formatDate(mov.AmsScrtEndDate) }}</span
                              >
                              <span v-if="!mov.AmsScrtBeginDate">Activo</span>
                            </v-col>
                            <v-col
                              cols="12"
                              sm="6"
                              v-if="
                                mov.TypeTerminationName ||
                                mov.AmsTerminationDate
                              "
                            >
                              <strong class="red--text text--darken-3"
                                >Cese:</strong
                              >
                              {{ mov.TypeTerminationName || "" }}
                              {{
                                mov.AmsTerminationDate
                                  ? "(" +
                                    formatDate(mov.AmsTerminationDate) +
                                    ")"
                                  : ""
                              }}
                            </v-col>
                            <v-col cols="12" v-if="mov.AmsServiceDescription">
                              <strong>Descripción Servicio:</strong>
                              {{ mov.AmsServiceDescription }}
                            </v-col>
                          </v-row>
                        </div>
                      </v-expand-transition>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
              </div>
            </div>
          </v-expand-transition>
        </div>

        <!-- Paginación -->
        <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            circle
            color="primary"
          />
        </div>

        <!-- Modal de Formatos a Firmar -->
        <contract-formats-modal
          v-model="showFormatsModal"
          :contract="selectedContractForFormats"
        />
      </div>
    </div>
  </div>
</template>

<script>
import _sCttoService from "@/services/HumanResource/HmnContractService.js";
import ContractFormatsModal from "@/views/HumanResource/PdfContract/ContractFormatsModal.vue";

export default {
  name: "ContractTimeline",

  components: {
    ContractFormatsModal,
  },

  props: {
    contracts: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedAgtID: {
      type: Number,
      default: null,
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
  },

  data() {
    return {
      activeTab: 0,
      currentPage: 1,
      expandedContracts: [],
      movements: {},
      loadingMovements: {},
      loadingAnyMovement: false,
      expandedFullFields: [], // Guarda los AmsID que tienen expandido los campos completos
      fullFieldsVisibility: {},
      showFormatsModal: false,
      selectedContractForFormats: null,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.contracts.length / this.itemsPerPage) || 1;
    },
    paginatedContracts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.contracts.slice(start, start + this.itemsPerPage);
    },
    positionsTimeline() {
      if (!this.contracts || this.contracts.length === 0) return [];

      const formatIso = (val) => {
        if (!val) return "";
        if (this.$moment) {
          return this.$moment(val).format("YYYY-MM-DD");
        }
        try {
          return new Date(val).toISOString().substring(0, 10);
        } catch {
          return String(val).substring(0, 10);
        }
      };

      // 1. Ordenar contratos cronológicamente (más antiguo al más reciente)
      // Se toma la fecha real de inicio: si tiene movimientos, el primer movimiento cronológico (menor AmsID)
      const sortedContracts = [...this.contracts]
        .filter((c) => c && (c.AgtBeginDate || (this.movements[c.AgtID] && this.movements[c.AgtID].length > 0)))
        .sort((a, b) => {
          const movsA = this.movements[a.AgtID];
          const movsB = this.movements[b.AgtID];
          const firstMovA = movsA && movsA.length > 0
            ? [...movsA].sort((x, y) => (Number(x.AmsID) || 0) - (Number(y.AmsID) || 0))[0]
            : null;
          const firstMovB = movsB && movsB.length > 0
            ? [...movsB].sort((x, y) => (Number(x.AmsID) || 0) - (Number(y.AmsID) || 0))[0]
            : null;

          const startA = (firstMovA && firstMovA.AmsBeginDate) || a.AgtBeginDate;
          const startB = (firstMovB && firstMovB.AmsBeginDate) || b.AgtBeginDate;

          const tA = new Date(startA).getTime() || 0;
          const tB = new Date(startB).getTime() || 0;
          return tA - tB;
        });

      // 2. Extraer los tramos de cargo de cada contrato respetando modificaciones internas y renovaciones
      const rawSlices = [];

      sortedContracts.forEach((c) => {
        const movs = this.movements[c.AgtID];
        if (movs && movs.length > 0) {
          // Ordenar movimientos por AmsID ascendente (cronológico de creación)
          const sortedMovs = [...movs].sort(
            (a, b) => (Number(a.AmsID) || 0) - (Number(b.AmsID) || 0)
          );

          // Movimiento inicial (el primero en crearse para este contrato)
          const originMov = sortedMovs[0];
          let currentPstID = Number(originMov.PstID) || Number(c.PstID) || 0;
          let currentPstName =
            originMov.PstName ||
            c.PstName ||
            (currentPstID ? `Puesto ID ${currentPstID}` : "Puesto no especificado");
          let currentAreName = originMov.AreName || c.AreName || "—";
          let currentCceName = originMov.CceName || c.CceName || "—";
          let currentSalary = originMov.AmsSalary || c.AgtSalary || "—";
          let currentStart = formatIso(originMov.AmsBeginDate || c.AgtBeginDate);
          let currentEnd = formatIso(originMov.AmsEndDate || c.AgtEndDate);
          let hadInternalChange = false;

          sortedMovs.forEach((m, idx) => {
            if (idx === 0) return;

            const mPstID = Number(m.PstID) || null;
            const mPstName = m.PstName || (mPstID ? `Puesto ID ${mPstID}` : null);
            const hasPstChange = mPstID
              ? (mPstID !== currentPstID)
              : (mPstName && mPstName.trim().toUpperCase() !== currentPstName.trim().toUpperCase());

            // Si cambió el cargo respecto al actual dentro del contrato
            if (hasPstChange) {
              // Determinar fecha efectiva del cambio
              let changeDate = null;
              if (
                m.AmsStartDateMovement &&
                formatIso(m.AmsStartDateMovement) !== formatIso(c.AgtBeginDate) &&
                formatIso(m.AmsStartDateMovement) !== currentStart
              ) {
                changeDate = formatIso(m.AmsStartDateMovement);
              } else if (m.SecCreate) {
                changeDate = formatIso(m.SecCreate);
              } else {
                changeDate = formatIso(m.AmsBeginDate);
              }

              // Cerrar tramo anterior un día antes de la vigencia del nuevo cargo
              const prevEndDate = this.$moment
                ? this.$moment(changeDate).subtract(1, "days").format("YYYY-MM-DD")
                : changeDate;

              rawSlices.push({
                AgtID: c.AgtID,
                PstID: currentPstID,
                PstName: currentPstName,
                AreName: currentAreName,
                CceName: currentCceName,
                Salary: currentSalary,
                StartDate: currentStart,
                EndDate: prevEndDate,
                AgtStatus: c.AgtStatus,
                AgtStatusName: c.AgtStatusName,
                isInternalChange: hadInternalChange,
              });

              // Iniciar nuevo tramo con el nuevo cargo
              currentPstID = mPstID || currentPstID;
              currentPstName = mPstName || currentPstName;
              currentAreName = m.AreName || currentAreName;
              currentCceName = m.CceName || currentCceName;
              currentSalary = m.AmsSalary || currentSalary;
              currentStart = changeDate;
              currentEnd = m.AmsEndDateMovement
                ? formatIso(m.AmsEndDateMovement)
                : (m.AmsEndDate ? formatIso(m.AmsEndDate) : currentEnd);
              hadInternalChange = true;
            } else {
              // Mismo puesto: en renovaciones (TypeAms 1) o ampliaciones (TypeAms 5), extiende vigencia
              if (m.TypeAms == 1 || m.TypeAms == 5) {
                if (m.AmsEndDate) {
                  const mEnd = formatIso(m.AmsEndDate);
                  if (new Date(mEnd) > new Date(currentEnd)) {
                    currentEnd = mEnd;
                  }
                }
              } else if (m.TypeAms == 4) {
                if (m.AmsTerminationDate || m.AmsEndDate) {
                  currentEnd = formatIso(m.AmsTerminationDate || m.AmsEndDate);
                }
              }
            }
          });

          // Empujar el tramo final del contrato
          rawSlices.push({
            AgtID: c.AgtID,
            PstID: currentPstID,
            PstName: currentPstName,
            AreName: currentAreName,
            CceName: currentCceName,
            Salary: currentSalary,
            StartDate: currentStart,
            EndDate: currentEnd,
            AgtStatus: c.AgtStatus,
            AgtStatusName: c.AgtStatusName,
            isInternalChange: hadInternalChange,
          });
        } else {
          // Si aún no se cargaron los movimientos, usar datos base del contrato
          rawSlices.push({
            AgtID: c.AgtID,
            PstID: Number(c.PstID) || 0,
            PstName:
              c.PstName || (c.PstID ? `Puesto ID ${c.PstID}` : "Puesto no especificado"),
            AreName: c.AreName || "—",
            CceName: c.CceName || "—",
            Salary: c.AgtSalary || "—",
            StartDate: formatIso(c.AgtBeginDate),
            EndDate: formatIso(c.AgtEndDate),
            AgtStatus: c.AgtStatus,
            AgtStatusName: c.AgtStatusName,
            isInternalChange: false,
          });
        }
      });

      // 3. Fusionar tramos contiguos del mismo cargo e identificar CORTES / INTERRUPCIONES
      const timeline = [];
      let currentGroup = null;

      rawSlices.forEach((slice) => {
        if (!currentGroup) {
          currentGroup = {
            ...slice,
            contracts: [slice.AgtID],
          };
          return;
        }

        // Evaluar continuidad entre currentGroup.EndDate y slice.StartDate
        const prevEndM = this.$moment
          ? this.$moment(currentGroup.EndDate)
          : new Date(currentGroup.EndDate);
        const nextStartM = this.$moment
          ? this.$moment(slice.StartDate)
          : new Date(slice.StartDate);

        // Diferencia en días entre fin del periodo anterior e inicio del siguiente
        let daysDiff = 0;
        if (this.$moment) {
          daysDiff = nextStartM.diff(prevEndM, "days");
        } else {
          daysDiff = Math.round(
            (nextStartM.getTime() - prevEndM.getTime()) / (1000 * 60 * 60 * 24)
          );
        }

        // Si hay una separación mayor a 2 días (ej: 21/09/2025 al 06/10/2025 = 15 días)
        const isCut = daysDiff > 2;

        if (isCut) {
          // 1. Cerrar y agregar el grupo actual
          timeline.push({ ...currentGroup, isCut: false });

          // 2. Insertar elemento de CORTE / INTERRUPCIÓN LABORAL
          timeline.push({
            isCut: true,
            StartDate: currentGroup.EndDate,
            EndDate: slice.StartDate,
            gapDays: daysDiff > 1 ? daysDiff - 1 : daysDiff,
            prevAgtID: currentGroup.contracts[currentGroup.contracts.length - 1],
            nextAgtID: slice.AgtID,
          });

          // 3. Iniciar nuevo grupo
          currentGroup = {
            ...slice,
            contracts: [slice.AgtID],
          };
        } else {
          // Es CONTINUO
          const isSamePst =
            (currentGroup.PstID && slice.PstID && currentGroup.PstID === slice.PstID) ||
            String(currentGroup.PstName).trim().toUpperCase() ===
              String(slice.PstName).trim().toUpperCase();

          if (isSamePst) {
            // FUSIONAR: extender EndDate del grupo actual si el nuevo tramo finaliza después
            const currentEndM = this.$moment
              ? this.$moment(currentGroup.EndDate)
              : new Date(currentGroup.EndDate);
            const sliceEndM = this.$moment
              ? this.$moment(slice.EndDate)
              : new Date(slice.EndDate);

            const isAfter = this.$moment
              ? sliceEndM.isAfter(currentEndM)
              : sliceEndM.getTime() > currentEndM.getTime();

            if (isAfter) {
              currentGroup.EndDate = slice.EndDate;
            }
            if (!currentGroup.contracts.includes(slice.AgtID)) {
              currentGroup.contracts.push(slice.AgtID);
            }
            // Actualizar status si el nuevo tramo es el activo
            if (slice.AgtStatus == 1) {
              currentGroup.AgtStatus = 1;
              currentGroup.AgtStatusName = slice.AgtStatusName;
            }
            if (slice.isInternalChange) {
              currentGroup.isInternalChange = true;
            }
          } else {
            // Cambio de cargo continuo (ej: de Programador a Desarrollador Jr)
            timeline.push({ ...currentGroup, isCut: false });
            currentGroup = {
              ...slice,
              contracts: [slice.AgtID],
            };
          }
        }
      });

      if (currentGroup) {
        timeline.push({ ...currentGroup, isCut: false });
      }

      // 4. Devolver la línea de tiempo en orden cronológico descendente (el más reciente arriba)
      return timeline.sort((a, b) => {
        const dA = new Date(a.StartDate).getTime() || 0;
        const dB = new Date(b.StartDate).getTime() || 0;
        return dB - dA;
      });
    },
  },

  watch: {
    contracts: {
      immediate: true,
      handler() {
        if (this.currentPage > this.totalPages) {
          this.currentPage = 1;
        }
        this.fetchAllMovements();
      },
    },
    activeTab(val) {
      if (val === 0 || val === 1) {
        this.fetchAllMovements();
      }
    },
  },

  mounted() {
    this.fetchAllMovements();
  },

  methods: {
    openFormatsModal(contract) {
      this.selectedContractForFormats = contract;
      this.showFormatsModal = true;
    },

    async fetchAllMovements(force = false) {
      if (!this.contracts || this.contracts.length === 0) return;
      this.loadingAnyMovement = true;
      try {
        await Promise.all(
          this.contracts.map((c) => {
            if (force || !this.movements[c.AgtID]) {
              return this.fetchMovements(c.AgtID, force);
            }
            return Promise.resolve();
          })
        );
      } finally {
        this.loadingAnyMovement = false;
      }
    },
    /**
     * Alternar expansión de contrato:
     * Siempre consulta al backend los movimientos más recientes por AgtID
     */
    async toggleContract(contract) {
      const agtID = contract.AgtID;
      const isExpanded = this.expandedContracts.includes(agtID);

      if (isExpanded) {
        this.expandedContracts = this.expandedContracts.filter(
          (id) => id !== agtID,
        );
        return;
      }

      this.expandedContracts.push(agtID);
      await this.fetchMovements(agtID);
    },

    /**
     * Consulta siempre los movimientos del contrato especificado
     */
    async fetchMovements(agtID, force = false) {
      if (!agtID) return;

      this.$set(this.loadingMovements, agtID, true);
      try {
        const userID =
          this.$fun && typeof this.$fun.getUserID === "function"
            ? this.$fun.getUserID()
            : 0;
        const res = await _sCttoService.movementsByAgtID(agtID, userID);
        const rawList = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || [];
        const hydratedList = rawList.map((mov) => {
          let merged = { ...mov };
          if (mov.AmsSnapshotJSON) {
            try {
              const snap =
                typeof mov.AmsSnapshotJSON === "string"
                  ? JSON.parse(mov.AmsSnapshotJSON)
                  : mov.AmsSnapshotJSON;
              if (snap && typeof snap === "object") {
                for (const key in snap) {
                  if (
                    snap[key] !== null &&
                    snap[key] !== "" &&
                    snap[key] !== undefined
                  ) {
                    merged[key] = snap[key];
                  }
                }
              }
              for (const key in mov) {
                if (
                  mov[key] !== null &&
                  mov[key] !== "" &&
                  mov[key] !== undefined
                ) {
                  merged[key] = mov[key];
                }
              }
            } catch (err) {
              console.warn("Error parsing snapshot JSON:", err);
            }
          }
          return merged;
        });
        this.$set(this.movements, agtID, hydratedList);
      } catch (e) {
        console.warn("Error al cargar movimientos de contrato:", e);
        if (force || !this.movements[agtID]) {
          this.$set(this.movements, agtID, []);
        }
      } finally {
        this.$set(this.loadingMovements, agtID, false);
      }
    },

    toggleShowFullFields(amsID, isInitial = false) {
      const current = this.isShowingFullFields(amsID, isInitial);
      this.$set(this.fullFieldsVisibility, amsID, !current);
    },

    isShowingFullFields(amsID, isInitial = false) {
      if (this.fullFieldsVisibility[amsID] !== undefined) {
        return !!this.fullFieldsVisibility[amsID];
      }
      return !!isInitial;
    },

    /**
     * Detecta si es el movimiento origen / inicial de la lista
     */
    isInitialMovement(mov, list, idx) {
      if (!list || list.length === 0) return true;
      if (
        mov.TypeAms === 3 ||
        (mov.TypeAmsName && mov.TypeAmsName.toUpperCase() === "ORIGEN")
      ) {
        return true;
      }
      return idx === list.length - 1;
    },

    /**
     * Compara el movimiento actual con el cronológicamente anterior (idx + 1)
     * y retorna la lista de campos que cambiaron.
     */
    getMovementDiffs(curr, list, idx) {
      if (!list || list.length === 0) return [];
      if (this.isInitialMovement(curr, list, idx)) return [];

      const prev = list[idx + 1];
      if (!prev) return [];

      const diffs = [];

      // Helper de comparación
      const checkDiff = (label, currVal, prevVal, currName, prevName) => {
        const cHasName =
          currName !== undefined &&
          currName !== null &&
          String(currName).trim() !== "";
        const pHasName =
          prevName !== undefined &&
          prevName !== null &&
          String(prevName).trim() !== "";

        const cVal = cHasName
          ? currName
          : currVal !== undefined && currVal !== null
          ? currVal
          : "";
        const pVal = pHasName
          ? prevName
          : prevVal !== undefined && prevVal !== null
          ? prevVal
          : "";

        const cNorm = cVal != null ? String(cVal).trim() : "";
        const pNorm = pVal != null ? String(pVal).trim() : "";

        if (cNorm !== pNorm && (cNorm || pNorm)) {
          diffs.push({
            label,
            oldVal: pNorm || "—",
            newVal: cNorm || "—",
          });
        }
      };

      checkDiff("Cargo", curr.PstID, prev.PstID, curr.PstName, prev.PstName);
      checkDiff("Área", curr.AreID, prev.AreID, curr.AreName, prev.AreName);
      checkDiff(
        "Centro de Costo",
        curr.CceID,
        prev.CceID,
        curr.CceName,
        prev.CceName,
      );
      checkDiff(
        "Tipo de Contrato",
        curr.TypeContract,
        prev.TypeContract,
        curr.TypeContractName,
        prev.TypeContractName,
      );
      checkDiff(
        "Categoría",
        curr.TypeCategory,
        prev.TypeCategory,
        curr.TypeCategoryName,
        prev.TypeCategoryName,
      );
      checkDiff(
        "Mod. Formativa",
        curr.TypeFormatLabor,
        prev.TypeFormatLabor,
        curr.TypeFormatLaborName,
        prev.TypeFormatLaborName,
      );
      checkDiff(
        "Régimen Laboral",
        curr.TypeLaborRegime,
        prev.TypeLaborRegime,
        curr.TypeLaborRegimeName,
        prev.TypeLaborRegimeName,
      );
      checkDiff(
        "Tipo Trabajador",
        curr.TypeWorker,
        prev.TypeWorker,
        curr.TypeWorkerName,
        prev.TypeWorkerName,
      );
      checkDiff(
        "Cat. Ocupacional",
        curr.TypeOccupationalCategory,
        prev.TypeOccupationalCategory,
        curr.TypeOccupationalCategoryName,
        prev.TypeOccupationalCategoryName,
      );
      checkDiff(
        "Turno",
        curr.TypeTurno,
        prev.TypeTurno,
        curr.TypeTurnoName,
        prev.TypeTurnoName,
      );
      checkDiff("Campaña", curr.CmpID, prev.CmpID, curr.CmpName, prev.CmpName);
      checkDiff(
        "Cuadrilla",
        curr.GngID,
        prev.GngID,
        curr.GngName,
        prev.GngName,
      );
      checkDiff(
        "Línea de Negocio",
        curr.BslID,
        prev.BslID,
        curr.BslName,
        prev.BslName,
      );
      checkDiff(
        "Pensión",
        curr.TypePensionRegimen,
        prev.TypePensionRegimen,
        curr.TypePensionRegimenName,
        prev.TypePensionRegimenName,
      );
      checkDiff("CUSPP", curr.CUSPP, prev.CUSPP, curr.CUSPP, prev.CUSPP);
      checkDiff(
        "Salud",
        curr.TypeHealthRegimen,
        prev.TypeHealthRegimen,
        curr.TypeHealthRegimenName,
        prev.TypeHealthRegimenName,
      );
      checkDiff(
        "Compañía EPS",
        curr.TypeCompany,
        prev.TypeCompany,
        curr.TypeCompanyName,
        prev.TypeCompanyName,
      );
      checkDiff(
        "Banco",
        curr.TypeEntityBank,
        prev.TypeEntityBank,
        curr.TypeEntityBankName,
        prev.TypeEntityBankName,
      );
      checkDiff(
        "N° Cuenta Bancaria",
        curr.BatAccountNumber,
        prev.BatAccountNumber,
        curr.BatAccountNumber,
        prev.BatAccountNumber,
      );
      checkDiff(
        "CCI Bancario",
        curr.BatCCI,
        prev.BatCCI,
        curr.BatCCI,
        prev.BatCCI,
      );

      // Fechas de Contrato
      const cBegin = this.formatDate(curr.AmsBeginDate);
      const pBegin = this.formatDate(prev.AmsBeginDate);
      if (cBegin !== pBegin) {
        diffs.push({
          label: "Fecha Inicio Contrato",
          oldVal: pBegin,
          newVal: cBegin,
        });
      }

      const cEnd = this.formatDate(curr.AmsEndDate);
      const pEnd = this.formatDate(prev.AmsEndDate);
      if (cEnd !== pEnd) {
        diffs.push({ label: "Fecha Fin Contrato", oldVal: pEnd, newVal: cEnd });
      }

      // SCTR
      const cSctr = !!curr.AgtIsSCTR;
      const pSctr = !!prev.AgtIsSCTR;
      if (cSctr !== pSctr) {
        diffs.push({
          label: "SCTR",
          oldVal: pSctr ? "Activo" : "Inactivo",
          newVal: cSctr ? "Activo" : "Inactivo",
        });
      }

      // Cese
      if (curr.TypeTermination || curr.AmsTerminationDate) {
        const cTerm = this.formatDate(curr.AmsTerminationDate);
        const pTerm = this.formatDate(prev.AmsTerminationDate);
        if (cTerm !== pTerm) {
          diffs.push({
            label: "Fecha Cese",
            oldVal: pTerm,
            newVal: cTerm,
          });
        }
        checkDiff(
          "Motivo Cese",
          curr.TypeTermination,
          prev.TypeTermination,
          curr.TypeTerminationName,
          prev.TypeTerminationName,
        );
      }

      // Sueldo (si cambió el valor encriptado/representativo)
      if (
        curr.AmsSalary &&
        prev.AmsSalary &&
        curr.AmsSalary !== prev.AmsSalary
      ) {
        diffs.push({
          label: "Sueldo / Jornal",
          oldVal: "Anterior",
          newVal: "Actualizado",
        });
      }

      return diffs;
    },

    // ────────────────────────────────────────────────────────
    // HELPERS DE FORMATO Y ESTILOS
    // ────────────────────────────────────────────────────────
    formatPeriodDuration(start, end) {
      if (!start) return "";
      const s = this.$moment ? this.$moment(start) : null;
      const e = end
        ? this.$moment
          ? this.$moment(end)
          : null
        : this.$moment
        ? this.$moment()
        : null;
      if (!s || !e || !s.isValid() || !e.isValid()) return "";
      if (e.isBefore(s)) return "";

      const sClone = s.clone();
      const years = e.diff(sClone, "years");
      sClone.add(years, "years");
      const months = e.diff(sClone, "months");
      sClone.add(months, "months");
      const days = e.diff(sClone, "days") + 1; // periodo inclusivo

      const parts = [];
      if (years > 0) parts.push(`${years} ${years === 1 ? "año" : "años"}`);
      if (months > 0) parts.push(`${months} ${months === 1 ? "mes" : "meses"}`);
      if (days > 0 || parts.length === 0)
        parts.push(`${days} ${days === 1 ? "día" : "días"}`);

      return parts.join(", ");
    },

    formatDate(d) {
      if (!d) return "—";
      return this.$fun.formatDateView
        ? this.$fun.formatDateView(d)
        : d.toString().substring(0, 10);
    },

    formatDateTime(dt) {
      if (!dt) return "—";
      try {
        const dateObj = new Date(dt);
        return dateObj.toLocaleString("es-PE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return dt;
      }
    },

    getStatusLabel(status) {
      const map = { 0: "Registrado", 1: "Activo", 2: "Cesado" };
      return map[status] !== undefined ? map[status] : "Registrado";
    },

    getStatusColor(status) {
      if (status == 0) return "amber lighten-4";
      if (status == 1) return "green lighten-4";
      if (status == 2) return "red lighten-4";
      return "blue lighten-4";
    },

    getStatusTextColor(status) {
      if (status == 0) return "amber darken-4";
      if (status == 1) return "green darken-3";
      if (status == 2) return "red darken-3";
      return "blue darken-3";
    },

    /**
     * Mapeo de Nombre de Tipo de Movimiento (DefID 1535)
     */
    getMovementTypeName(mov) {
      if (mov.TypeAmsName) return mov.TypeAmsName;
      const map = {
        1: "RENOVACIÓN",
        2: "MODIFICACIÓN",
        3: "ORIGEN",
        4: "CESE",
        5: "AMPLIACIÓN",
        6: "ELIMINACIÓN",
      };
      return map[mov.TypeAms] || `MOV #${mov.TypeAms || 1}`;
    },

    /**
     * Soporta código hexadecimal directo de DedFormat (TypeAmsColor)
     * o fallback a paleta armónica.
     */
    resolveMovementColor(mov) {
      if (mov.TypeAmsColor && typeof mov.TypeAmsColor === "string") {
        const clean = mov.TypeAmsColor.trim();
        if (clean.startsWith("#") || clean.startsWith("rgb")) {
          return clean;
        }
      }

      const map = {
        1: "teal darken-1", // Renovación
        2: "amber darken-2", // Modificación
        3: "indigo darken-1", // Origen / Alta
        4: "red darken-1", // Cese
        5: "purple darken-1", // Ampliación
        6: "grey darken-1", // Eliminación
      };
      return map[mov.TypeAms] || "blue-grey darken-1";
    },

    getCustomHexStyle(mov) {
      if (mov.TypeAmsColor && typeof mov.TypeAmsColor === "string") {
        const clean = mov.TypeAmsColor.trim();
        if (clean.startsWith("#") || clean.startsWith("rgb")) {
          return {
            backgroundColor: clean + " !important",
            color: "#ffffff !important",
          };
        }
      }
      return {};
    },

    getMovementIcon(typeAms) {
      const map = {
        1: "mdi-autorenew", // Renovación
        2: "mdi-pencil-box-multiple", // Modificación
        3: "mdi-file-document-plus", // Origen
        4: "mdi-account-remove", // Cese
        5: "mdi-calendar-plus", // Ampliación
        6: "mdi-delete", // Eliminación
      };
      return map[typeAms] || "mdi-circle-medium";
    },

    async confirmDeleteMovement(mov, agtID) {
      if (!mov || !mov.AmsID) return;

      const confirm = await this.$fun.alert(
        `¿Está seguro de anular el Movimiento #${
          mov.AmsID
        } (${this.getMovementTypeName(
          mov,
        )})? Esta acción no se puede deshacer.`,
        "question",
      );

      if (!confirm.value) return;

      try {
        const res = await _sCttoService.deleteMovement(
          mov.AmsID,
          this.$fun.getUserID(),
        );
        if (res?.data?.success !== false) {
          this.$fun.alert("Movimiento anulado correctamente.", "success");
          await this.fetchMovements(agtID, true);
          this.$emit("refresh");
        } else {
          this.$fun.alert(
            res?.data?.message || "No se pudo anular el movimiento.",
            "error",
          );
        }
      } catch (err) {
        console.error("Error al anular movimiento:", err);
        this.$fun.alert(
          err?.response?.data?.Message ||
            err?.message ||
            "Error al anular el movimiento.",
          "error",
        );
      }
    },

    isIndeterminate(contract) {
      if (!contract) return false;
      if (!contract.AgtEndDate) return true;
      if (contract.TypeSituation == 1) return true;
      const typeName = (contract.TypeContractName || "").toLowerCase();
      if (typeName.includes("indeterminado") || typeName.includes("indefinido")) return true;
      return false;
    },
  },
};
</script>

<style scoped>
.contract-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.2s ease;
}

.contract-card:hover {
  border-color: #bdbdbd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.contract-card--active {
  border-left: 4px solid #4caf50;
}

.contract-card--inactive {
  border-left: 4px solid #9e9e9e;
}

.contract-card--selected {
  border: 1.5px solid #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

.contract-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot-active {
  background-color: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
}

.dot-inactive {
  background-color: #9e9e9e;
}

.movements-container {
  background: #fafbfc;
  border-top: 1px dashed #e0e0e0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.movements-timeline >>> .v-timeline-item__dot {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.movement-detail-card {
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e8ecef;
}

/* Sección de Diferencias / Cambios */
.diff-box {
  background: #fffdf5;
  border: 1px solid #ffe082;
}

.diff-badge {
  background: #ffffff;
  border: 1px solid #ffd54f;
  border-radius: 4px;
  font-size: 11px;
}

.diff-field-name {
  color: #795548;
  font-weight: 600;
}

.diff-old-val {
  color: #8d6e63;
  text-decoration: line-through;
}

.diff-new-val {
  color: #2e7d32;
}

/* Nota de Origen */
.initial-box {
  background: #f0f4ff;
  border: 1px solid #c5cae9;
}

.bg-movement-fields {
  background-color: #f4f6f8;
  border: 1px solid #eef1f4;
}

.bg-light {
  background-color: #f5f7fa;
}

.tracking-wider {
  letter-spacing: 0.5px;
}
</style>
